import { IConditionProps, IFormState } from "@ngt/forms-core";
import { useCallback, useContext } from "react";
import { ConditionType, FormFieldDisplayCondition, IFormField, SiteEventType, Species, SpeciesForm, User } from "../../api/dtos/dtos";
import AuthContext from "../../contexts/auth/AuthContext";
import SpeciesFormsContext from "../../contexts/data/SpeciesFormsContext";
import MapFormContext from "../../contexts/data/MapFormContext";

//
// --- Condition Configuration Types ---
//
interface IConditionConfig {
    conditionType: ConditionType
}
interface IEqualConditionConfig extends IConditionConfig {
    conditionType: ConditionType.Equal,
    targetName: string,
    targetValue: string,
    meta: {
        value: string;
    }
}
interface INotEqualConditionConfig extends IConditionConfig {
    conditionType: ConditionType.NotEqual,
    targetName: string,
    targetValue: string,
    meta: {
        value: string;
    }
}

interface IGreaterThanConditionConfig extends IConditionConfig {
    conditionType: ConditionType.GreaterThan,
    targetName: string,
    targetValue: string,
    meta: {
        value: string;
    }
}

interface IGreaterThanOrEqualConditionConfig extends IConditionConfig {
    conditionType: ConditionType.GreaterThanOrEqual,
    targetName: string,
    targetValue: string,
    meta: {
        value: string;
    }
}

interface ILessThanConditionConfig extends IConditionConfig {
    conditionType: ConditionType.LessThan,
    targetName: string,
    targetValue: string,
    meta: {
        value: string;
    }
}

interface ILessThanOrEqualConditionConfig extends IConditionConfig {
    conditionType: ConditionType.LessThanOrEqual,
    targetName: string,
    targetValue: string,
    meta: {
        value: string;
    }
}

interface ICustomConditionConfig extends IConditionConfig {
    conditionType: ConditionType.Custom,
    targetName: string,
    targetValue: string,
    meta: {
        functionName: string,
        [U: string]: string
    }
}

//
// --- Types ---
//
interface IFieldCondition<TCondition extends IConditionConfig = any> {
    (formValue: any, selectedForm: SpeciesForm, condition: TCondition, user: User | null, isEditMode: boolean): boolean;
}

//
// --- Condition Function Record ---
//
const conditionFunctions: Record<ConditionType, IFieldCondition> = {
    [ConditionType.Equal]: (formValue: any, selectedForm: SpeciesForm, condition: IEqualConditionConfig, _user: User | null, _isEditMode: boolean) => {
        let ret = true;
        switch (typeof formValue[selectedForm.name]?.[condition?.targetName]) {
            case "boolean":
                ret = formValue[selectedForm.name]?.[condition?.targetName] === (condition?.targetValue === "true");
                break;
            
            case "number": 
                const number = parseFloat(condition?.targetValue);
                if (isNaN(number)){
                    ret = false;
                    break;
                }

                ret = formValue[selectedForm.name]?.[condition?.targetName] === number;
                break;

            case "string":
            ret = formValue[selectedForm.name]?.[condition?.targetName] === condition?.targetValue;
                break;
            
            case "bigint":
            case "symbol":
            case "undefined":
            case "object":
            case "function":
                ret = false;
                break;

            default: 
                ret = false;
                break;
        }
        return ret
    },
    [ConditionType.NotEqual]: (formValue: any, selectedForm: SpeciesForm, condition: INotEqualConditionConfig, _user: User | null, _isEditMode: boolean) => { 
        let ret = true;

        switch (typeof formValue[selectedForm.name]?.[condition?.targetName]) {
            case "boolean":
                ret = formValue[selectedForm.name]?.[condition?.targetName] !== (condition?.targetValue === "true");
                break;
            
            case "number": 
                const number = parseFloat(condition?.targetValue);
                if (isNaN(number)){
                    ret = false;
                    break;
                }

                ret = formValue[selectedForm.name]?.[condition?.targetName] !== number;
                break;

            case "string": 
            ret = formValue[selectedForm.name]?.[condition?.targetName] !== condition?.targetValue;
                break;
            
            case "bigint":
            case "symbol":
            case "undefined":
            case "object":
            case "function":
                ret = false;
                break;

            default: 
                ret = false;
                break;
        }
        return ret
    },
    [ConditionType.GreaterThan]: (formValue: any, selectedForm: SpeciesForm, condition: IGreaterThanConditionConfig, _user: User | null, _isEditMode: boolean) => { 
        let ret = true;

        switch (typeof formValue[selectedForm.name]?.[condition?.targetName]) {
            case "boolean":
                ret = formValue[selectedForm.name]?.[condition?.targetName] === (condition?.targetValue === "true");
                break;
            
            case "number": 
                const number = parseFloat(condition?.targetValue);
                if (isNaN(number)){
                    ret = false;
                    break;
                }

                ret = formValue[selectedForm.name]?.[condition?.targetName] === number;
                break;

            case "string": 
            ret = formValue[selectedForm.name]?.[condition?.targetName] === condition?.targetValue;
                break;
            
            case "bigint":
            case "symbol":
            case "undefined":
            case "object":
            case "function":
                ret = false;
                break;

            default: 
                ret = false;
                break;
        }
        return ret
    },
    [ConditionType.GreaterThanOrEqual]: (formValue: any, selectedForm: SpeciesForm, condition: IGreaterThanOrEqualConditionConfig, _user: User | null, _isEditMode: boolean) => { 
        let ret = true;

        switch (typeof formValue[selectedForm.name]?.[condition?.targetName]) {
            case "boolean":
                ret = formValue[selectedForm.name]?.[condition?.targetName] === (condition?.targetValue === "true");
                break;
            
            case "number": 
                const number = parseFloat(condition?.targetValue);
                if (isNaN(number)){
                    ret = false;
                    break;
                }

                ret = formValue[selectedForm.name]?.[condition?.targetName] === number;
                break;

            case "string": 
            ret = formValue[selectedForm.name]?.[condition?.targetName] === condition?.targetValue;
                break;
            
            case "bigint":
            case "symbol":
            case "undefined":
            case "object":
            case "function":
                ret = false;
                break;

            default: 
                ret = false;
                break;
        }
        return ret
    },
    [ConditionType.LessThan]: (formValue: any, selectedForm: SpeciesForm, condition: ILessThanConditionConfig, _user: User | null, _isEditMode: boolean) => {
        let ret = true;

        switch (typeof formValue[selectedForm.name]?.[condition?.targetName]) {
            case "boolean":
                ret = formValue[selectedForm.name]?.[condition?.targetName] === (condition?.targetValue === "true");
                break;
            
            case "number": 
                const number = parseFloat(condition?.targetValue);
                if (isNaN(number)){
                    ret = false;
                    break;
                }

                ret = formValue[selectedForm.name]?.[condition?.targetName] === number;
                break;

            case "string": 
            ret = formValue[selectedForm.name]?.[condition?.targetName] === condition?.targetValue;
                break;
            
            case "bigint":
            case "symbol":
            case "undefined":
            case "object":
            case "function":
                ret = false;
                break;

            default: 
                ret = false;
                break;
        }
        return ret
    },
    [ConditionType.LessThanOrEqual]: (formValue: any, selectedForm: SpeciesForm, condition: ILessThanOrEqualConditionConfig, _user: User | null, _isEditMode: boolean) => {
        let ret = true;

        switch (typeof formValue[selectedForm.name]?.[condition?.targetName]) {
            case "boolean":
                ret = formValue[selectedForm.name]?.[condition?.targetName] === (condition?.targetValue === "true");
                break;
            
            case "number": 
                const number = parseFloat(condition?.targetValue);
                if (isNaN(number)){
                    ret = false;
                    break;
                }

                ret = formValue[selectedForm.name]?.[condition?.targetName] === number;
                break;

            case "string": 
            ret = formValue[selectedForm.name]?.[condition?.targetName] === condition?.targetValue;
                break;
            
            case "bigint":
            case "symbol":
            case "undefined":
            case "object":
            case "function":
                ret = false;
                break;

            default: 
                ret = false;
                break;
        }
        return ret
    },
    [ConditionType.Custom]: (formValue: any, selectedForm: SpeciesForm, condition: ICustomConditionConfig, user: User | null, isEditMode: boolean) => {
        return callCustomFunctions(formValue, selectedForm, condition, user, isEditMode);
    },
}


//
// --- Custom Function ---
//
const UnauthenticatedUser = (formValue: any, form: SpeciesForm, condition: ICustomConditionConfig, user: User | null, isEditMode: boolean) => {
    // Show if admin is editing a record
    if (isEditMode && AdministratorUser(formValue, form, condition, user, isEditMode) && formValue?.[form.name]?.UserId == null) {
        return true;
    }

    if (!user){
        return true;
    }
    return false;
};
const AuthenticatedUser = (_formValue: any, _form: SpeciesForm, _condition: ICustomConditionConfig, user: User | null, _isEditMode: boolean) => {
    if (user){
        return true;
    }
    return false;
};

const AdministratorUser = (_formValue: any, form: SpeciesForm, _condition: ICustomConditionConfig, user: User | null, _isEditMode: boolean) => {
    if (user?.roles?.includes("admin") || user?.roles?.includes("superadmin")){
        return true;
    }
    else if (form.speciesType === Species.Rabbit && form.siteEventType === SiteEventType.Disease && user?.roles?.includes("rabbit_disease_k5_admin")) {
        return true;
    }
    else if (form.speciesType === Species.NewPests && form.siteEventType === SiteEventType.Sighting && user?.roles?.includes("new_incursion_admin")) {
        return true;
    }
    else if (form.speciesType === Species.Toad && user?.roles?.includes("toadscan_admin")) {
        return true;
    }
    return false;
};

const CoordinatesState = (formValue: any, form: SpeciesForm, condition: ICustomConditionConfig, _user: User | null, _isEditMode: boolean) => {
    if (!form?.name || !formValue[form.name] || !formValue[form.name]?.['meta'] || !formValue[form.name]?.['meta']?.['state']){
        return false;
    }

    if (condition.meta['Operation'] == "NotEqual") {
        if (formValue[form.name]?.['meta']?.['state'] != condition.meta['State']) {
            return true;
        }
        return false;   
    }

    if (formValue[form.name]?.['meta']?.['state'] === condition.meta['State']){
        return true;
    }
    
    return false;
};

// 
// --- Custom Functions Records ---
//
const customFunctions: Record<string, IFieldCondition> = {
    "UnauthenticatedUser": UnauthenticatedUser,
    "AuthenticatedUser": AuthenticatedUser,
    "AdministratorUser": AdministratorUser,
    "CoordinatesState": CoordinatesState
}

const callCustomFunctions = (formValue: any, form: SpeciesForm, condition: ICustomConditionConfig, user: User | null, isEditMode: boolean): boolean => {
    const customFunction = customFunctions[condition.meta.Key];

    if (customFunction) {
        return customFunction(formValue, form, condition, user, isEditMode);
    }

    return false;
}

export const useFormFieldCondition = () => {
    const { user } = useContext(AuthContext);
    const { selectedForm, selectedFormFields } = useContext(SpeciesFormsContext);
    const {  recordToEdit } = useContext(MapFormContext);

    // Get display condition for a single form field
    const getFormFieldDisplayCondition = useCallback((speciesFormField: IFormField) => {
        if (!speciesFormField.conditions || speciesFormField.conditions.length === 0) {
            return undefined;
        }

        if(!selectedForm || !selectedFormFields){
            return undefined;
        }

        const cFn: IConditionProps<SpeciesForm>["condition"] = ({ value }) => {
            const formValue = value as any;
            if (!speciesFormField?.conditions || speciesFormField?.conditions?.length === 0){
                return true;
            }
            for (let formDisplayCondition of speciesFormField.conditions) {
                const conditionFunction = conditionFunctions[formDisplayCondition.condition];
    
                let childrenRes: boolean = execChilrenCondition(selectedForm, selectedFormFields, formValue, formDisplayCondition.children ?? []);
                let res: boolean = conditionFunction(formValue, selectedForm, formDisplayCondition, user, recordToEdit != null) && childrenRes;
    
                if (res){
                    return true;
                }
            }
            return false;
        }

        return cFn;

    }, [user, selectedForm, conditionFunctions]);

    const execChilrenCondition = (form: SpeciesForm, selectedFormFields: Record<string, IFormField>, formValue: Record<string, any>,  childConditions: FormFieldDisplayCondition[]): boolean => {

        if (!childConditions || childConditions?.length === 0){
            return true;
        }

        for (let cond of childConditions) {

            const conditionFunction = conditionFunctions[cond.condition];

            let childrenRes: boolean = execChilrenCondition(form, selectedFormFields, formValue, cond.children ?? []);
            let res: boolean = conditionFunction(formValue, selectedForm!, cond, user, recordToEdit != null) && childrenRes;
            
            if (res){
                return true;
            }
        }

        return false;
    }

    // Get display conditions for multiple form fields
    const getFormFieldDisplayConditions = useCallback((speciesFormFields: IFormField[] | undefined) => {

        if (!speciesFormFields){
            return undefined;
        }

        let res: Record<string, IConditionProps<SpeciesForm>["condition"] | undefined> = {};

        for (let field of speciesFormFields) {
            res[`${field.id}-${field.fieldType}`] = getFormFieldDisplayCondition(field);
        }

        return res;

    }, [getFormFieldDisplayCondition]);

    // Get display conditions for multiple form fields as a boolean value
    const getFormFieldDisplayConditionsValue = useCallback((formState: IFormState<Record<string, any>, string>) => {

        if (!formState || !formState.value){
            return undefined;
        }

        if (!selectedFormFields || !selectedForm){
            return undefined;
        }

        const formValue = formState.value;

        let res: Record<string, boolean> = {};

        res = Object.keys(formState.value[selectedForm.name]).reduce<Record<string, boolean>>((a, b) => {
            if (!selectedFormFields[b]){
                return a;
            }
            if (!selectedFormFields[b]?.conditions || selectedFormFields[b]?.conditions?.length === 0){
                a[b] = true;
            }

            for (let formDisplayCondition of (selectedFormFields[b].conditions ?? [])) {
                const conditionFunction = conditionFunctions[formDisplayCondition.condition];
    
                let childrenRes: boolean = execChilrenCondition(selectedForm, selectedFormFields, formValue, formDisplayCondition.children ?? []);
                let result: boolean = conditionFunction(formValue, selectedForm, formDisplayCondition, user, recordToEdit != null) && childrenRes;

                if (result){
                    a[b] = true;
                    break;
                }
            }

            if(!a[b]){
                a[b] = false;
            }

            return a;
        }, {});

        return res;

    }, [getFormFieldDisplayCondition, selectedForm, user]);

    // Get overall display condition for multiple form fields
    const getOverallFormFieldDisplayConditions = useCallback((speciesFormFields: IFormField[] | undefined) => {

        if (!speciesFormFields || !selectedForm || !selectedFormFields) {
            return () => true;
        }
    
        const cFn: IConditionProps<SpeciesForm>["condition"] = ({ value }) => {
            const formValue = value as any;
    
            for (let field of speciesFormFields) {
                if (!field?.conditions || field?.conditions?.length === 0){
                    return true;
                }
                for (let formDisplayCondition of field.conditions) {
                    const conditionFunction = conditionFunctions[formDisplayCondition.condition];
        
                    let childrenRes: boolean = execChilrenCondition(selectedForm, selectedFormFields, formValue, formDisplayCondition.children ?? []);
                    let res: boolean = conditionFunction(formValue, selectedForm, formDisplayCondition, user, recordToEdit != null) && childrenRes;
        
                    if (res){
                        return true;
                    }
                }
            }
            return false;
        };
    
        return cFn;
    
    }, [selectedForm, user]);

    // Get a single form field condition value
    const getFormFieldDisplayConditionValue = useCallback((speciesFormField: IFormField, formState: IFormState<Record<string, any>, string>): boolean | undefined => {
        if (!formState?.value) {
            return undefined;
        }

        if (!selectedForm || !selectedFormFields) {
            return undefined;
        }

        // No conditions = always visible
        if (!speciesFormField.conditions || speciesFormField.conditions.length === 0) {
            return true;
        }

        const formValue = formState.value;

        for (let formDisplayCondition of speciesFormField.conditions) {
            const conditionFunction = conditionFunctions[formDisplayCondition.condition];

            const childrenRes = execChilrenCondition(
                selectedForm,
                selectedFormFields,
                formValue,
                formDisplayCondition.children ?? []
            );

            const result = conditionFunction(formValue, selectedForm, formDisplayCondition, user, recordToEdit != null) && childrenRes;

            if (result) {
                return true;
            }
        }

        return false;
    },
    [user, selectedForm, selectedFormFields, conditionFunctions]
);

    return {
        getFormFieldDisplayCondition,
        getFormFieldDisplayConditions,
        getOverallFormFieldDisplayConditions,
        getFormFieldDisplayConditionsValue,
        getFormFieldDisplayConditionValue
    }

}

export default useFormFieldCondition;